import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { baseURL, headers } from '@/v2/constants/requests'

const QUERY_KEY = ['SettingsAppointmentTypes']
const EMPTY_ARRAY = []

export const useSettingsAppointmentTypes = () => {
  const { data, isLoading, isError } = useQuery({
    queryKey: QUERY_KEY,
    queryFn: async () => {
      const res = await fetch(`${baseURL}/v2/rest/settings/appointment_types`, {
        method: 'GET',
        headers,
      })
      if (!res.ok) {
        throw new Error("We couldn't load the appointment types")
      }
      return res.json()
    },
  })

  return { appointmentTypes: data ?? EMPTY_ARRAY, isLoading, isError }
}

export const useAddAppointmentType = () => {
  const queryClient = useQueryClient()

  const { mutateAsync: addAppointmentType, ...rest } = useMutation({
    mutationFn: async typeData => {
      const res = await fetch(`${baseURL}/v2/rest/settings/appointment_types`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ appointment_type: typeData }),
      })
      if (!res.ok) {
        const error = await res.json()
        throw new Error(error.errors || 'Could not create appointment type')
      }
      return res.json()
    },
    onSuccess: () => {
      queryClient.invalidateQueries(QUERY_KEY)
    },
  })

  return { addAppointmentType, ...rest }
}

export const useUpdateAppointmentType = () => {
  const queryClient = useQueryClient()

  const { mutateAsync: updateAppointmentType, ...rest } = useMutation({
    mutationFn: async ({ id, ...typeData }) => {
      const res = await fetch(
        `${baseURL}/v2/rest/settings/appointment_types/${id}`,
        {
          method: 'PATCH',
          headers,
          body: JSON.stringify({ appointment_type: typeData }),
        }
      )
      if (!res.ok) {
        const error = await res.json()
        throw new Error(error.errors || 'Could not update appointment type')
      }
      return res.json()
    },
    onSuccess: () => {
      queryClient.invalidateQueries(QUERY_KEY)
    },
  })

  return { updateAppointmentType, ...rest }
}

export const useDeleteAppointmentType = () => {
  const queryClient = useQueryClient()

  const { mutateAsync: deleteAppointmentType, ...rest } = useMutation({
    mutationFn: async id => {
      const res = await fetch(
        `${baseURL}/v2/rest/settings/appointment_types/${id}`,
        {
          method: 'DELETE',
          headers,
        }
      )
      if (!res.ok) {
        const error = await res.json()
        throw new Error(error.errors || 'Could not delete appointment type')
      }
      return res.status === 204 ? {} : res.json()
    },
    onSuccess: () => {
      queryClient.invalidateQueries(QUERY_KEY)
    },
  })

  return { deleteAppointmentType, ...rest }
}
